import GlobalStyles from '@mui/material/GlobalStyles';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { createAppTheme } from '@/theme';

import {
    applyThemeToDocument,
    getStoredTheme,
    getSystemTheme,
    type ResolvedTheme,
    resolveTheme,
    storeTheme,
    subscribeToSystemTheme,
    ThemeContext,
    type ThemePreference,
} from '.';

interface ThemeProviderProps {
    children: ReactNode;
}

/**
 * Owns the light/dark/system preference; MUI follows the theme it resolves to. The system theme is its own state,
 * so a `system` preference tracks the OS live.
 *
 * No CssBaseline: normalize.css already resets the page, and CssBaseline would restyle its typography and margins.
 * The global styles paint only what the palette cannot reach — the body behind the layout, and in the dark theme
 * the bare markdown links, which would keep the browser's #0000EE.
 */
const ThemeProvider = ({ children }: ThemeProviderProps) => {
    const [preference, setPreferenceState] = useState<ThemePreference>(getStoredTheme);
    const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);
    const resolvedTheme = resolveTheme(preference, systemTheme);

    const setPreference = useCallback((next: ThemePreference) => {
        storeTheme(next);
        setPreferenceState(next);
    }, []);

    useEffect(() => subscribeToSystemTheme(() => setSystemTheme(getSystemTheme())), []);

    useEffect(() => {
        applyThemeToDocument(resolvedTheme);
    }, [resolvedTheme]);

    const value = useMemo(
        () => ({ preference, resolvedTheme, setPreference }),
        [preference, resolvedTheme, setPreference],
    );

    const muiTheme = useMemo(() => createAppTheme(resolvedTheme), [resolvedTheme]);

    return (
        <ThemeContext.Provider value={value}>
            <MuiThemeProvider theme={muiTheme}>
                <GlobalStyles
                    styles={(theme) => ({
                        body: { backgroundColor: theme.palette.secondary.main },
                        ...(theme.palette.mode === 'dark' && { a: { color: theme.palette.primary.main } }),
                    })}
                />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

export default ThemeProvider;
